import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const isMissing = (value) =>
  !value || value === "undefined" || value === "null";

export const getStoredUserProfile = () => {
  try {
    const raw = localStorage.getItem("linko_user_profile");
    return raw && raw !== "undefined" && raw !== "null" ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const resolveDisplayName = (data) => {
  if (!data) return "";
  return (
    data.name ||
    (data.firstName && data.lastName
      ? `${data.firstName} ${data.lastName}`
      : data.firstName) ||
    (typeof data.email === "string" ? data.email.split("@")[0] : "") ||
    ""
  );
};

export const readStoredName = () => {
  const profile = getStoredUserProfile();
  const nameFromProfile = resolveDisplayName(profile);
  if (nameFromProfile) return nameFromProfile;
  const name = localStorage.getItem("name");
  if (!isMissing(name)) return name;
  const username = localStorage.getItem("username");
  if (!isMissing(username)) return username;
  return "";
};

export const readStoredGroupName = () => {
  const profile = getStoredUserProfile();
  const org = profile?.trainingGroupId?.groupName;
  if (!isMissing(org)) return org;
  const groupname = localStorage.getItem("groupname");
  if (!isMissing(groupname)) return groupname;
  return "";
};

export const readStoredImage = () => {
  const profile = getStoredUserProfile();
  if (profile?.profileImage && !isMissing(profile.profileImage)) return profile.profileImage;
  if (profile?.trainingGroupId?.groupImage && !isMissing(profile.trainingGroupId.groupImage)) return profile.trainingGroupId.groupImage;
  const profileImage = localStorage.getItem("profileImage");
  if (!isMissing(profileImage)) return profileImage;
  const groupImage = localStorage.getItem("groupImage");
  if (!isMissing(groupImage)) return groupImage;
  return null;
};

export const readStoredEmail = () => {
  const profile = getStoredUserProfile();
  if (profile?.email && !isMissing(profile.email)) return profile.email;
  const email = localStorage.getItem("email");
  if (!isMissing(email)) return email;
  return "";
};

export const readStoredPlanName = () => {
  const profile = getStoredUserProfile();
  if (profile?.plan_name && !isMissing(profile.plan_name)) return profile.plan_name;
  const plan = localStorage.getItem("plan_name");
  if (!isMissing(plan)) return plan;
  return "";
};

export const readStoredIsSubscription = () => {
  const profile = getStoredUserProfile();
  if (profile && typeof profile.is_subscription === "boolean") return profile.is_subscription;
  return localStorage.getItem("is_subscription") === "true";
};

export const readStoredUserId = () => {
  const profile = getStoredUserProfile();
  if (profile?._id && !isMissing(profile._id)) return profile._id;
  const userId = localStorage.getItem("userId");
  if (!isMissing(userId)) return userId;
  return "";
};

export const readStoredMasterPrompt = () => {
  const profile = getStoredUserProfile();
  const mp = profile?.masterPrompt || profile?.master_prompt || profile?.trainingGroupId?.masterPrompt || profile?.trainingGroupId?.master_prompt;
  if (mp && !isMissing(mp)) return mp;
  const stored = localStorage.getItem("masterPrompt");
  if (!isMissing(stored)) return stored;
  return "";
};

export const persistProfileToStorage = (data) => {
  if (!data) return;

  // Single source of truth: linko_user_profile
  try {
    const existing = getStoredUserProfile() || {};
    const merged = { ...existing, ...data };
    localStorage.setItem("linko_user_profile", JSON.stringify(merged));
  } catch (_e) {}

  // Clean up duplicate individual keys
  localStorage.removeItem("name");
  localStorage.removeItem("username");
  localStorage.removeItem("email");
  localStorage.removeItem("profileImage");
  localStorage.removeItem("groupname");
  localStorage.removeItem("groupImage");
  localStorage.removeItem("userId");
  localStorage.removeItem("masterPrompt");
  localStorage.removeItem("plan_name");
  localStorage.removeItem("is_subscription");

  const isOrgMember =
    !isMissing(data.trainingGroupId?.groupName) ||
    Boolean(data.trainingGroupId) ||
    (data.plan_name && (data.plan_name.toLowerCase().includes("org") || data.plan_name.toLowerCase().includes("adminprovide")));

  if (isOrgMember) {
    localStorage.removeItem("linko_contact_sent");
    localStorage.removeItem("linko_rejection_note");
  } else if (data.note) {
    localStorage.setItem("linko_rejection_note", data.note);
    localStorage.removeItem("linko_contact_sent");
  } else if (data.note === null || data.note === "") {
    localStorage.removeItem("linko_rejection_note");
    if (data.is_contactus === true || data.is_contact_request === true || data.contact_request === true || data.is_request === true) {
      localStorage.setItem("linko_contact_sent", "true");
    } else if (data.is_contactus === false || data.is_contact_request === false || data.contact_request === false || data.is_request === false) {
      localStorage.removeItem("linko_contact_sent");
    }
  }
};

export function useSidebarData() {
  const reduxUser = useSelector((state) => state.user?.user);
  const [username, setUsername] = useState(readStoredName);
  const [groupname, setGroupName] = useState(readStoredGroupName);
  const [imagePreview, setImagePreview] = useState(readStoredImage);

  const loadData = () => {
    setUsername(readStoredName());
    setGroupName(readStoredGroupName());
    setImagePreview(readStoredImage());
  };

  useEffect(() => {
    loadData();
    window.addEventListener("profileUpdated", loadData);
    return () => {
      window.removeEventListener("profileUpdated", loadData);
    };
  }, [reduxUser]);

  return {
    username,
    groupname,
    imagePreview,
    refetchSidebarData: loadData,
  };
}
